import { ArrowRight01Icon, ViewIcon, ViewOffSlashIcon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { useState } from "react";
import { useLocation } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import { auth } from "@/firebase";
import { getAuth, GoogleAuthProvider, sendEmailVerification, signInWithPopup } from "firebase/auth";
import { Box, Typography, TextField, Stack, Button, Alert, InputAdornment, IconButton, Divider } from "@mui/material";
import AppConfig from "@/components/AppConfig";
import { login } from "@/features/auth/slice";
import { useAppDispatch } from "@/hooks";
import useRoutes from "@/hooks/useRoutes";
import useSnackbar from "@/hooks/useSnackbar";
import { Configs } from "@/constants/configs";
import routes from "@/constants/routes";
import GoogleIcon from "@/assets/icons/google-icon.svg";

const signInSchema = Yup.object().shape({
  email: Yup.string().email("Correo no válido").required("Campo requerido"),
  password: Yup.string().min(2, "Muy corto").max(30, "Muy Largo").required("Campo requerido"),
});

const SignInForm = () => {
  const location = useLocation();
  const [navigate] = useRoutes();
  const dispatch = useAppDispatch();
  const [openSnackbar] = useSnackbar();
  const [userNotVerified, setUserNotVerified] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [alert, setAlert] = useState({
    message: "Correo no verificado",
  });

  //ToDo: make a hook of Google SignIn...
  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
    } catch (error) {
      console.error("Google sign-in error:", error);
      openSnackbar("Error al iniciar con Google", "error");
    }
  };

  const formik = useFormik({
    initialValues: { email: "", password: "" },
    validationSchema: signInSchema,
    onSubmit: async ({ email, password }) => {
      setLoginError(null);

      try {
        const response = await dispatch(login({ email, password })).unwrap();

        if (response?.emailVerified) {
          navigate(location.state.prevPage || "organizations.main");
        } else {
          setUserNotVerified(email);
        }
      } catch (error: any) {
        let message = "Correo o contraseña incorrectos.";
        if (error.code === "auth/user-not-found") {
          message = "No existe una cuenta con este correo.";
        } else if (error.code === "auth/too-many-requests") {
          message = "Demasiados intentos. Intenta más tarde.";
        }

        setLoginError(message);
      }
    },
  });

  const verifyEmail = async () => {
    try {
      const auth = getAuth();
      if (auth) {
        await sendEmailVerification(auth.currentUser);
        setUserNotVerified(null);
      }
    } catch (error) {
      console.error(error);
      if (error?.code === "auth/too-many-requests") {
        setAlert({
          message: "Demasiados intentos, prueba mas tarde.",
        });
      }
    }
  };

  return (
    <Box>
      <form onSubmit={formik.handleSubmit}>
        <Stack
          spacing={3}
          sx={{
            display: "flex",
            flex: 1,
          }}
        >
          <Typography variant="h2">Iniciar Sesión</Typography>

          {userNotVerified ? (
            <Alert
              severity="warning"
              action={
                <Button size="small" onClick={verifyEmail}>
                  Verificar
                </Button>
              }
            >
              {alert.message}
            </Alert>
          ) : null}

          {loginError ? (
            <Alert severity="error" sx={{ mt: -1 }}>
              {loginError}
            </Alert>
          ) : null}

          <Stack spacing={2}>
            <TextField fullWidth name="email" type="email" label="Correo" margin="normal" value={formik.values.email} onChange={formik.handleChange} />
            <TextField
              name="password"
              label="Contraseña"
              type={showPassword ? "text" : "password"}
              margin="normal"
              value={formik.values.password}
              onChange={formik.handleChange}
              variant="outlined"
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)}>{showPassword ? <HugeiconsIcon icon={ViewOffSlashIcon} /> : <HugeiconsIcon icon={ViewIcon} />}</IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
            <Button size="large" variant="contained" type="submit" endIcon={<HugeiconsIcon icon={ArrowRight01Icon} />} disabled={formik.isSubmitting}>
              Iniciar Sesión
            </Button>
            <Divider>ó</Divider>

            <Button
              fullWidth
              size="large"
              onClick={signInWithGoogle}
              startIcon={<img src={GoogleIcon} alt="Google" />}
              sx={{
                color: "black",
                background: "linear-gradient(180deg, #ECF5FF 0%, #E4EFFF 100%)",
              }}
              disabled={formik.isSubmitting}
            >
              Ingresar con Google
            </Button>
            <AppConfig isEnabled={Configs.ACCOUNT_CREATION}>
              <Button size="large" variant="text" onClick={() => navigate(routes.signUp)}>
                Crear Cuenta
              </Button>
              <Divider>ó</Divider>

              <Button
                fullWidth
                size="large"
                onClick={signInWithGoogle}
                startIcon={<img src={GoogleIcon} alt="Google" />}
                sx={{
                  color: "black",
                  background: "linear-gradient(180deg, #ECF5FF 0%, #E4EFFF 100%)",
                }}
              >
                Ingresar con Google
              </Button>
              <Button size="large" variant="outlined" onClick={() => window.alert("Test")}>
                Facebook
              </Button>
            </AppConfig>
          </Stack>
        </Stack>
      </form>
    </Box>
  );
};

export default SignInForm;
